import React, { useState, useEffect } from 'react'

import { withRouter } from 'react-router-dom'
import { injectIntl } from 'react-intl'
import ReactTooltip from 'react-tooltip'

import Button from '../../components/common/button/Button'
import Icon from '../../components/common/icon/Icon'
import Tooltip from '../../components/common/tooltip/Tooltip'
import Personalization from '../../components/common/personalization/Personalization'
import LoadingSpinner from '../../components/common/spinner/LoadingSpinner'
import MessageFeedback from '../../components/common/messageFeedback/MessageFeedback'

import {
  getAggregateReportInitialConfig,
  getAggregateReportConfig,
  getAggregateReportStatus,
  getAggregateReportData,
  getAggregateReportMessages,
  getAggregateReportMessageDetails,
  getAggregateReportLinks,
  processAggregateReport,
  isFrontendError
} from './AggregateReportApi'
import AggregateReportAskPassword from './AggregateReportAskPassword'
import AggregateReportData from './AggregateReportData'
import AggregateReportMessages from './AggregateReportMessages'

import { dateStringToYmdHM } from '../../services/DateStringFormat'

import batchPromises from '../../services/batchPromises'
import AggregateReportLinks from './AggregateReportLinks'

const PAGE_STATUS_ASK_PASSWORD = 'PAGE_STATUS_ASK_PASSWORD'
const PAGE_STATUS_READY = 'PAGE_STATUS_READY'

const PAGE_STATUS_ERROR = 'PAGE_STATUS_ERROR'
const PAGE_STATUS_PARAMS_ERROR = 'PAGE_STATUS_PARAMS_ERROR'

const PAGE_STATUS_LOADING = 'PAGE_STATUS_LOADING'
const PAGE_STATUS_POLLING = 'PAGE_STATUS_POLLING'

const initialPageState = {
  intialConfig: undefined,
  //
  pwd: '',
  askPassword: false,
  passwordInvalid: false,
  reportConfig: undefined,
  pageStatus: undefined,
  //
  reportStatus: undefined,
  reportValidity: false
}

const initialAggregateDataState = {
  error: false,
  loading: false,
  data: {}
}

const initialCollectionState = {
  loading: false,
  loaded: false,
  error: false,
  items: undefined,
  pageSize: 5,
  pageNumber: 1,
  totalItems: 0
}

const REPORT_STATUS_DONE = 0
const REPORT_STATUS_REQUESTED = 1
const REPORT_STATUS_IN_PROGRESS = 2
const REPORT_STATUS_ERROR = 3
const REPORT_STATUS_NOT_FOUND = 4

const POLLING_INTERVAL = 5 * 1000

const AggregateReport = ({ match, history, location, intl }) => {
  const [pageState, setPageState] = useState(initialPageState)
  const [aggregateDataState, setAggregateDataState] = useState(initialAggregateDataState)
  const [messagesState, setMessagesState] = useState(initialCollectionState)
  const [linksState, setLinksState] = useState(initialCollectionState)

  const reportId = location.search // encoded url

  // on render
  useEffect(() => {
    ReactTooltip.rebuild()
  })

  //  on mount
  useEffect(() => {
    reloadPage()

    // on unmount
    return () => { ReactTooltip.rebuild() }
  }, [])

  //  on update report status and validity
  useEffect(() => {
    if (pageState.reportValidity && (pageState.reportStatus === REPORT_STATUS_DONE)) {
      if (pageState.reportConfig && pageState.reportConfig.ShowAggregateData) {
        reloadAggregateData()
      }
      if (pageState.reportConfig && pageState.reportConfig.ShowMessages) {
        reloadMessages()
      }
      if (pageState.reportConfig && pageState.reportConfig.ShowLinks) {
        reloadLinks()
      }
    }
  }, [pageState.reportConfig, pageState.reportStatus, pageState.reportValidity])

  // on update messages details
  useEffect(() => {
    if (messagesState.loaded && Array.isArray(messagesState.items)) {
      const msgIds = messagesState.items.map(o => o.IdMessage)

      batchPromises(msgIds, 10, updateItemsInMessagesList)
    }
  }, [messagesState.loaded])

  // on update links details
  useEffect(() => {
    if (linksState.loaded && Array.isArray(linksState.items)) {
      const msgIds = Array.from(new Set([...linksState.items.map(o => o.IdMessage)]))

      batchPromises(msgIds, 10, updateItemsInLinksList)
    }
  }, [linksState.loaded])

  function getNextStepFromStatusAndValidity (status, isValid) {
    switch (status) {
      case REPORT_STATUS_DONE:
        return (isValid)
          ? { newPageStatus: PAGE_STATUS_READY, nextAction: undefined }
          : { newPageStatus: PAGE_STATUS_LOADING, nextAction: processReport }

      case REPORT_STATUS_IN_PROGRESS:
      case REPORT_STATUS_REQUESTED:
        return { newPageStatus: PAGE_STATUS_POLLING, nextAction: checkStatus }
      case REPORT_STATUS_ERROR:
      case REPORT_STATUS_NOT_FOUND:
        return { newPageStatus: PAGE_STATUS_LOADING, nextAction: processReport }
    }
  }

  function onPasswordChange (e) {
    const v = e.target.value
    setPageState((prevState) => ({ ...prevState, pwd: v }))
  }

  function onPasswordSubmit (e) {
    reloadReportConfig()
  }

  function reloadPage () {
    setPageState((prevState) => ({ ...initialPageState, pageStatus: PAGE_STATUS_LOADING }))
    getAggregateReportInitialConfig(reportId).then((res) => {
      const error = (res === null) || isFrontendError(res)
      const newStatus = (error) ? PAGE_STATUS_PARAMS_ERROR : ((res.HasPassword) ? PAGE_STATUS_ASK_PASSWORD : PAGE_STATUS_LOADING)
      setPageState((prevState) => ({
        ...prevState,
        pageStatus: newStatus,
        intialConfig: res
      }))
      if (!error && !res.HasPassword) {
        reloadReportConfig()
      }
    }).catch((e) => {
      setPageState((prevState) => ({ ...prevState, pageStatus: PAGE_STATUS_ERROR }))
    })
  }

  function reloadReportConfig () {
    setPageState((prevState) => ({ ...prevState, pageStatus: PAGE_STATUS_LOADING }))

    Promise.all([getAggregateReportConfig(reportId, pageState.pwd), getAggregateReportStatus(reportId, pageState.pwd), getAggregateReportData(reportId, pageState.pwd)])
      .then(([config, status, data]) => {
        if (isFrontendError(config)) {
          setPageState((prevState) => ({
            ...prevState,
            pageStatus: PAGE_STATUS_ASK_PASSWORD,
            passwordInvalid: true,
            errorMessage: intl.formatMessage({ id: 'Load.Statistics.PasswordIncorrect' })
          }))
        } else {
          const isValid = (data) ? evalReportValidity(data.ValidTo) : false
          const { newPageStatus, nextAction } = getNextStepFromStatusAndValidity(status, isValid)
          //
          setPageState((prevState) => ({
            ...prevState,
            reportConfig: config,
            pageStatus: newPageStatus,
            //
            reportStatus: status,
            reportValidity: isValid
          }))

          if (nextAction) {
            nextAction()
          }
        }
      })
      .catch(() => {
        setPageState((prevState) => ({ ...initialPageState, pageStatus: PAGE_STATUS_ERROR }))
      })
  }

  function reloadAggregateData () {
    setAggregateDataState({ ...initialAggregateDataState, loading: true })
    getAggregateReportData(reportId, pageState.pwd)
      .then((res) => {
        setAggregateDataState((prevState) => ({ ...prevState, loading: false, data: res }))
      })
      .catch(() => {
        setAggregateDataState((prevState) => ({ ...prevState, loading: false, error: true }))
      })
  }
  function processReport () {
    setPageState((prevState) => ({ ...prevState, pageStatus: PAGE_STATUS_LOADING }))
    processAggregateReport(reportId, pageState.pwd)
      .then(() => {
        checkStatus()
      })
      .catch(() => {
        setPageState((prevState) => ({ ...prevState, pageStatus: PAGE_STATUS_ERROR }))
      })
  }

  function checkStatus () {
    setPageState((prevState) => ({ ...prevState, pageStatus: PAGE_STATUS_POLLING }))
    setTimeout(() => {
      getAggregateReportStatus(reportId, pageState.pwd)
        .then((status) => {
          const { newPageStatus, nextAction } = evalNextCheck(status)
          setPageState((prevState) => ({ ...prevState, pageStatus: newPageStatus, reportStatus: status, reportValidity: true }))

          if (nextAction) {
            nextAction()
          }
        })
        .catch(() => {
          setPageState((prevState) => ({ ...prevState, pageStatus: PAGE_STATUS_ERROR }))
        })
    }, POLLING_INTERVAL)
  }

  function evalNextCheck (status) {
    switch (status) {
      case REPORT_STATUS_DONE:
        return { newPageStatus: PAGE_STATUS_READY, nextAction: undefined }
      case REPORT_STATUS_IN_PROGRESS:
      case REPORT_STATUS_REQUESTED:
        return { newPageStatus: PAGE_STATUS_POLLING, nextAction: checkStatus }
      case REPORT_STATUS_ERROR:
      case REPORT_STATUS_NOT_FOUND:
        return { newPageStatus: PAGE_STATUS_ERROR, nextAction: undefined }
    }
  }

  function evalReportValidity (dateTimeString) {
    return dateTimeString ? (Date.now() < new Date(dateTimeString).getTime()) : false
  }

  function reloadMessages () {
    loadMessages(messagesState.pageSize, messagesState.pageNumber)
  }

  function loadMessages (pPageSize, pPageNumber) {
    setMessagesState((prevState) => ({
      ...prevState,
      loading: true,
      loaded: false,
      error: false,
      items: [],
      loadingNext: (pPageSize === prevState.pageSize) && (pPageNumber > prevState.pageNumber),
      loadingPrev: (pPageSize === prevState.pageSize) && (pPageNumber < prevState.pageNumber),
      pagerDisabled: true

    }))

    getAggregateReportMessages(reportId, pageState.pwd, pPageSize, pPageNumber)
      .then((res) => {
        setMessagesState((prevState) => ({
          ...prevState,
          loading: false,
          loaded: true,
          error: false,
          items: res.Items,
          totalItems: res.TotalItems,
          pageSize: pPageSize,
          pageNumber: pPageNumber,
          loadingNext: false,
          loadingPrev: false,
          pagerDisabled: false

        }))
      }).catch(() => {
        setMessagesState((prevState) => ({ ...prevState, loading: false, loaded: false, error: true }))
      })
  }

  function onChangeMessagesPager (pager) {
    loadMessages(pager.pageSize, pager.pageNumber)
  }

  function updateItemsInMessagesList (idMessage) {
    return getAggregateReportMessageDetails(reportId, pageState.pwd, idMessage)
      .then((msgDetails) => {
        setMessagesState((prevState) => ({
          ...prevState,
          items: prevState.items.map((o) => ((o.IdMessage === idMessage) ? { ...o, Subject: msgDetails.Subject } : o))
        }))
        return idMessage
      })
      .catch(() => {
        setMessagesState((prevState) => ({
          ...prevState,
          items: prevState.items.map((o) => ((o.IdMessage === idMessage) ? { ...o, error: true } : o))
        }))
        return idMessage
      })
  }

  function updateItemsInLinksList (idMessage) {
    return getAggregateReportMessageDetails(reportId, pageState.pwd, idMessage)
      .then((msgDetails) => {
        setLinksState((prevState) => ({
          ...prevState,
          items: prevState.items.map((o) => ((o.IdMessage === idMessage) ? { ...o, Subject: msgDetails.Subject } : o))
        }))
        return idMessage
      })
      .catch(() => {
        setLinksState((prevState) => ({
          ...prevState,
          items: prevState.items.map((o) => ((o.IdMessage === idMessage) ? { ...o, error: true } : o))
        }))
        return idMessage
      })
  }

  function reloadLinks () {
    loadLinks(linksState.pageSize, linksState.pageNumber)
  }

  function loadLinks (pPageSize, pPageNumber) {
    setLinksState((prevState) => ({
      ...prevState,
      loading: true,
      loaded: false,
      error: false,
      items: [],
      loadingNext: (pPageSize === prevState.pageSize) && (pPageNumber > prevState.pageNumber),
      loadingPrev: (pPageSize === prevState.pageSize) && (pPageNumber < prevState.pageNumber),
      pagerDisabled: true
    }))

    getAggregateReportLinks(reportId, pageState.pwd, pPageSize, pPageNumber)
      .then((res) => {
        setLinksState((prevState) => ({
          ...prevState,
          loading: false,
          loaded: true,
          error: false,
          items: res.Items,
          totalItems: res.TotalItems,
          pageSize: pPageSize,
          pageNumber: pPageNumber,
          loadingNext: false,
          loadingPrev: false,
          pagerDisabled: false
        }))
      }).catch(() => {
        setLinksState((prevState) => ({ ...prevState, loading: false, loaded: false, error: true }))
      })
  }

  function onChangeLinksPager (pager) {
    loadLinks(pager.pageSize, pager.pageNumber)
  }

  const askPasswordProps = {
    pwd: pageState.pwd,
    passwordInvalid: pageState.passwordInvalid,
    onChangePwd: onPasswordChange,
    onSubmit: onPasswordSubmit,
    errorMessage: pageState.errorMessage
  }

  const personalizationProps = {
    idList: pageState.reportConfig ? `${pageState.reportConfig.ListId}` : undefined,
    showLogo: true,
    applyCustomCSS: true
  }

  const tooltipProps = {
    className: 'frontend-p-left',
    tooltipText: intl.formatMessage({ id: 'Load.Statistics.ReportUpdateTooltip' }),
    family: 'sli',
    icon: 'question'
  }

  const printButtonProps = {
    kind: 'main',
    onClick: () => { window.print() },
    className: 'test-AggregateReportPubb-printButton'
  }

  const aggregateDataProps = {
    onReload: reloadAggregateData
  }

  const reportMessagesProps = {
    onChangePager: onChangeMessagesPager,
    onReload: reloadMessages
  }

  const reportLinksProps = {
    onChangePager: onChangeLinksPager,
    onReload: reloadLinks
  }

  const report = pageState.reportConfig

  return (
    <div className='frontend-wrapper-xl'>
      {(pageState.pageStatus === PAGE_STATUS_LOADING) &&
        <LoadingSpinner />}

      {(pageState.pageStatus === PAGE_STATUS_POLLING) &&
        <LoadingSpinner>{intl.formatMessage({ id: 'Load.Statistics.AggregateReportViewCheckStatusMsg' })}</LoadingSpinner>}

      {(pageState.pageStatus === PAGE_STATUS_PARAMS_ERROR) &&
        <MessageFeedback kind='error'>{intl.formatMessage({ id: 'Load.Statistics.ErrorDisplayingReport' })}</MessageFeedback>}

      {(pageState.pageStatus === PAGE_STATUS_ERROR) &&
        <MessageFeedback kind='error'>
          {intl.formatMessage({ id: 'Load.Statistics.ErrorLoadingData' })}
          <a className='frontend-p-left-xs-thin test-AggregateReportPubb-reloadPage' onClick={reloadPage}>{intl.formatMessage({ id: 'Load.MailupCommon.ReloadData' })}</a>
        </MessageFeedback>}

      {(pageState.pageStatus === PAGE_STATUS_ASK_PASSWORD) &&
        <AggregateReportAskPassword {...askPasswordProps} />}

      {(pageState.pageStatus === PAGE_STATUS_READY) &&
        <div>
          {report && report.ListId && <Personalization {...personalizationProps} />}
          <Tooltip />

          {report &&
            <div>
              <div className='frontend-row frontend-row_reset row-flex'>
                <div className='col-xs-9 frontend-p-left-0'>
                  <h1>{intl.formatMessage({ id: 'Load.Statistics.AggregateReport' })}</h1>
                </div>
                <div className='col-xs-3 frontend-p-right-0 frontend-text-right'>
                  <Button {...printButtonProps}>{intl.formatMessage({ id: 'Load.Common.Print' })}</Button>
                </div>
              </div>

              <div className='frontend-row frontend-row_reset frontend-box frontend-p-bottom'>
                <div className='frontend-p-left-0'>
                  <p className='frontend-m-bottom-x-sm'><strong>{intl.formatMessage({ id: 'Load.Common.NomeReport' })}</strong> {report.Title}</p>
                  <p>
                    <strong>{intl.formatMessage({ id: 'Load.Common.CreationDate' })}</strong> {dateStringToYmdHM(report.CreationDateJs.StringValue)}
                    <strong className='frontend-p-left'>{intl.formatMessage({ id: 'Load.MailupCommon.LastUpdate' })}</strong> {dateStringToYmdHM(report.LastUpdateDateJs.StringValue)}
                    <Icon {...tooltipProps} />

                  </p>
                </div>

              </div>

              {report.ShowAggregateData && <AggregateReportData {...aggregateDataState} {...aggregateDataProps} />}

              {report.ShowMessages && messagesState.items && <AggregateReportMessages {...messagesState} {...reportMessagesProps} />}

              {report.ShowLinks && linksState.items && <AggregateReportLinks {...linksState} {...reportLinksProps} />}

              <div className='frontend-row frontend-text-right'>
                <Button {...printButtonProps}>{intl.formatMessage({ id: 'Load.Common.Print' })}</Button>
              </div>

            </div>}
        </div>}

    </div>
  )
}

export default injectIntl(withRouter(AggregateReport))
